/* =====================================================================
   <hp-product-card> — beauty-led homepage card
   ---------------------------------------------------------------------
   Upgrades snippets/hp-product-card.liquid. Progressive: the SSR markup
   already shows the first colour's first frame; this element builds the
   swipeable gallery, wires the swatches to drive both the gallery and
   every link, and (spotlight only) ties the hero's gallery to page
   scroll. No dependencies.

   Interaction model
     · Gallery = native horizontal scroll-snap. Touch swipes; desktop gets
       hover arrows; a scroll-linked rail beneath the image shows position.
     · Swatch change swaps the gallery to that colour's shots, resets to
       frame 1, and retargets the title, every slide link, and the
       spotlight CTA to that variant.
     · Spotlight scroll-scrub: while the hero is in view and untouched,
       page-scroll progress drives its gallery. VERTICAL scroll drives it;
       only a horizontal swipe / wheel / arrow / dot / swatch hands control
       back to the user (permanently, so the two never fight). Off under
       prefers-reduced-motion.
   ===================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, min, max) { return n < min ? min : n > max ? max : n; }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  var CHEVRON = {
    prev: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.5 3.5 6 8l4.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5.5 3.5 10 8l-4.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  var LABELS = { prev: 'Previous image', next: 'Next image' };

  class HpCardElement extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.viewport = this.querySelector('[data-hp-viewport]');
      this.frame = this.querySelector('.hp-card__frame');
      var dataEl = this.querySelector('[data-hp-colors]');
      if (!this.viewport || !this.frame || !dataEl) return;

      try { this.colors = JSON.parse(dataEl.textContent) || []; }
      catch (e) { this.colors = []; }
      if (!this.colors.length) return;

      this.sizes = this.getAttribute('data-sizes') || '';
      this.spotlight = this.hasAttribute('spotlight');
      this.index = 0;                     // current slide
      this.colorIndex = this._checkedColor();
      this._took = false;                 // user has taken over the scrub

      this._buildControls();
      this._renderColor(this.colorIndex, true);
      this._wireSwatches();
      this._setupRail();
      this._wireViewport();

      if (this.spotlight && !REDUCE) this._armScrub();
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this.io) this.io.disconnect();
    }

    /* ---- setup ---- */

    _checkedColor() {
      var checked = this.querySelector('.hp-card__swatch-input:checked');
      var i = checked ? parseInt(checked.getAttribute('data-index'), 10) : 0;
      return isNaN(i) ? 0 : i;
    }

    _buildControls() {
      this.prevBtn = this._arrow('prev');
      this.nextBtn = this._arrow('next');
      this.frame.appendChild(this.prevBtn);
      this.frame.appendChild(this.nextBtn);

      this.dotsWrap = document.createElement('div');
      this.dotsWrap.className = 'hp-card__dots';
      this.dotsWrap.setAttribute('aria-hidden', 'true');

      // The fill is one element that slides, not a class hopping between segments:
      // it's driven straight off scrollLeft, so it tracks the image travelling above
      // it frame-for-frame instead of snapping when the rounded index flips.
      this.fill = document.createElement('span');
      this.fill.className = 'hp-card__fill';
      this.dotsWrap.appendChild(this.fill);

      // Sits outside the frame, directly beneath the image — the image plane is
      // already carrying the badges, so the position rail stays off it.
      this.frame.insertAdjacentElement('afterend', this.dotsWrap);

      this.live = document.createElement('span');
      this.live.className = 'sr-only';
      this.live.setAttribute('role', 'status');
      this.live.setAttribute('aria-live', 'polite');
      this.appendChild(this.live);
    }

    _arrow(dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hp-card__arrow hp-card__arrow--' + dir;
      b.setAttribute('aria-label', LABELS[dir]);
      b.innerHTML = CHEVRON[dir];
      b.addEventListener('click', function (e) {
        e.preventDefault();
        this._takeover();
        this._go(dir === 'next' ? 1 : -1);
      }.bind(this));
      return b;
    }

    /* ---- rendering ---- */

    _slide(m, href, eager) {
      var a = document.createElement('a');
      a.className = 'hp-card__slide';
      a.href = href;
      a.tabIndex = -1;
      a.setAttribute('aria-hidden', 'true');
      a.setAttribute('data-hp-slide', '');
      a.setAttribute('data-instant', '');
      var img = document.createElement('img');
      img.className = 'hp-card__img';
      // ORDER IS LOAD-BEARING. Everything that governs the fetch has to be set
      // before src/srcset: assigning src starts the request there and then, with
      // whatever `loading` was at that instant (the default, eager), and a later
      // `loading = 'lazy'` cannot call it back. Written the wrong way round, the
      // mobile chart downloaded all 28 slides it had just hidden.
      // `sizes` before `srcset` for the same reason — it feeds the candidate pick.
      img.loading = eager ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.alt = '';
      if (m.w) img.width = m.w;
      if (m.h) img.height = m.h;
      if (this.sizes) img.sizes = this.sizes;
      // A missing media file must never show a broken-image icon: fall back to
      // the frame's tint so the slide reads as an (empty) product frame. Bound
      // before src so a fast 404 can't beat the listener.
      img.addEventListener('error', function () { img.style.visibility = 'hidden'; });
      if (m.ss) img.srcset = m.ss;
      img.src = m.s;
      a.appendChild(img);
      return a;
    }

    _renderColor(ci, keepFirst) {
      var color = this.colors[ci];
      if (!color) return;
      var media = color.m || [];
      if (!media.length) return;

      // On first render the SSR slide 0 is already the initial colour's featured
      // image (the LCP). keepFirst is only passed on that initial call, where ci
      // === this.colorIndex, so keep the SSR frame and append the rest — even when
      // a colour facet made the initial colour a non-zero swatch (else we'd throw
      // away the painted image and re-fetch). Unfiltered, colorIndex is 0, so this
      // is identical to before.
      var canKeep = keepFirst && ci === this.colorIndex && this.viewport.children.length >= 1;
      if (canKeep) {
        while (this.viewport.children.length > 1) this.viewport.lastElementChild.remove();
        var first = this.viewport.firstElementChild;
        if (first) first.href = color.u;
        for (var i = 1; i < media.length; i++) {
          this.viewport.appendChild(this._slide(media[i], color.u, false));
        }
      } else {
        var frag = document.createDocumentFragment();
        for (var j = 0; j < media.length; j++) {
          frag.appendChild(this._slide(media[j], color.u, j === 0));
        }
        this.viewport.replaceChildren(frag);
      }

      this.index = 0;
      this.viewport.scrollLeft = 0;
      this._renderDots(media.length);
      this._syncControls();
    }

    _renderDots(count) {
      this.dotCount = count;
      this.dotsWrap.replaceChildren(this.fill);
      if (count <= 1) { this.dotsWrap.hidden = true; return; }
      this.dotsWrap.hidden = false;
      // Segments are the hit targets only; the fill above them carries the state.
      for (var i = 0; i < count; i++) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'hp-card__dot';
        d.tabIndex = -1;
        (function (idx, self) {
          d.addEventListener('click', function () { self._takeover(); self._goTo(idx); });
        })(i, this);
        this.dotsWrap.appendChild(d);
      }
      // One count drives both the fill's width and the CSS notch interval, so the
      // dividers always land exactly on the fill's edges.
      this.dotsWrap.style.setProperty('--hp-count', count);
      this.fill.style.width = (100 / count) + '%';
      this._syncFill();
    }

    // Maps scroll progress onto the rail. Continuous, so a swipe drags the fill
    // and a smooth scrollTo glides it — the rail and the image move as one.
    _syncFill() {
      if (!this.fill || !this.dotCount || this.dotCount <= 1) return;
      var max = this.viewport.scrollWidth - this.viewport.clientWidth;
      var p = max > 0 ? clamp(this.viewport.scrollLeft / max, 0, 1) : 0;
      this.fill.style.transform = 'translateX(' + (p * (this.dotCount - 1) * 100) + '%)';
    }

    _syncControls() {
      var last = (this.dotCount || 1) - 1;
      if (this.prevBtn) this.prevBtn.hidden = this.index <= 0;
      if (this.nextBtn) this.nextBtn.hidden = this.index >= last;
      this._syncFill();
    }

    /* ---- navigation ---- */

    _go(delta) {
      var last = (this.dotCount || 1) - 1;
      this._goTo(clamp(this.index + delta, 0, last));
    }

    _goTo(i) {
      this.index = i;
      this.viewport.scrollTo({ left: i * this.viewport.clientWidth, behavior: REDUCE ? 'auto' : 'smooth' });
      this._syncControls();
    }

    _wireViewport() {
      var self = this, ticking = false;
      this.viewport.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var w = self.viewport.clientWidth || 1;
          var i = clamp(Math.round(self.viewport.scrollLeft / w), 0, (self.dotCount || 1) - 1);
          // Fill first and every frame — the arrows only care about the rounded index.
          self._syncFill();
          if (i !== self.index) { self.index = i; self._syncControls(); }
        });
      }, { passive: true });

      // Horizontal intent = user takes over the scrub. Vertical is left to drive it.
      this.viewport.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) self._takeover();
      }, { passive: true });

      var sx = 0, sy = 0;
      this.viewport.addEventListener('touchstart', function (e) {
        var t = e.touches[0]; sx = t.clientX; sy = t.clientY;
      }, { passive: true });
      this.viewport.addEventListener('touchmove', function (e) {
        var t = e.touches[0];
        if (Math.abs(t.clientX - sx) > Math.abs(t.clientY - sy) && Math.abs(t.clientX - sx) > 8) self._takeover();
      }, { passive: true });

      this._onResize = debounce(function () {
        self.viewport.scrollLeft = self.index * self.viewport.clientWidth;
        self._layoutSwatches();
      }, 150);
      window.addEventListener('resize', this._onResize);
    }

    /* ---- swatches ---- */

    _wireSwatches() {
      var self = this;
      var inputs = this.querySelectorAll('.hp-card__swatch-input');
      Array.prototype.forEach.call(inputs, function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          var ci = parseInt(input.getAttribute('data-index'), 10);
          if (isNaN(ci)) return;
          self._takeover();
          self._selectColor(ci);
        });
      });
    }

    _selectColor(ci) {
      var color = this.colors[ci];
      if (!color) return;
      this.colorIndex = ci;
      this._renderColor(ci, false);

      var url = color.u;
      var title = this.querySelector('[data-hp-title]');
      if (title) title.href = url;
      var nameEl = this.querySelector('[data-hp-colorname]');
      if (nameEl) nameEl.textContent = color.n;

      var spot = this.closest('.hp-loved__spotlight');
      var cta = spot && spot.querySelector('[data-hp-cta]');
      if (cta) cta.href = url;

      if (this.live) this.live.textContent = color.n ? ('Showing ' + color.n) : '';
    }

    /* ---- swatch rail: +N overflow + name preview ---- */

    _setupRail() {
      var self = this;
      this.swatches = this.querySelector('.hp-card__swatches');
      this.colorname = this.querySelector('[data-hp-colorname]');
      if (!this.swatches) return;

      this._labels = Array.prototype.slice.call(this.querySelectorAll('.hp-card__swatch'));
      var inputs = Array.prototype.slice.call(this.querySelectorAll('.hp-card__swatch-input'));
      this._expanded = false;

      // +N control — appended once, shown/hidden by the layout pass.
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'hp-card__more';
      more.hidden = true;
      more.addEventListener('click', function () { self._expandRail(); });
      this.swatches.appendChild(more);
      this._moreBtn = more;

      // Hover / keyboard focus previews that colour's name in the caption.
      this._labels.forEach(function (label, i) {
        label.addEventListener('mouseenter', function () { self._previewName(i); });
        label.addEventListener('mouseleave', function () { self._restoreName(); });
      });
      inputs.forEach(function (input, i) {
        input.addEventListener('focus', function () { self._previewName(i); });
        input.addEventListener('blur', function () { self._restoreName(); });
      });

      requestAnimationFrame(function () { self._layoutSwatches(); });
    }

    // Collapse the rail to a single row, folding the overflow into a +N control.
    // Measures by wrap position, so it tracks whatever width the card actually has.
    _layoutSwatches() {
      if (this._expanded || !this._labels || !this._moreBtn) return;
      var labels = this._labels, more = this._moreBtn;

      for (var r = 0; r < labels.length; r++) labels[r].classList.remove('is-hidden');
      more.hidden = true;
      if (labels.length <= 2) return;

      var top0 = labels[0].offsetTop;
      var wrapAt = -1;
      for (var i = 1; i < labels.length; i++) {
        if (labels[i].offsetTop > top0 + 1) { wrapAt = i; break; }
      }
      if (wrapAt === -1) return;                       // everything fits on one row

      var visible = wrapAt > 1 ? wrapAt - 1 : 1;       // reserve a slot for +N
      if (this.colorIndex >= visible) { this._expandRail(); return; }  // keep selection visible

      for (var j = visible; j < labels.length; j++) labels[j].classList.add('is-hidden');
      more.hidden = false;

      // If the control itself wrapped, drop one more swatch to hold one row.
      if (more.offsetTop > top0 + 1 && visible > 1) {
        visible--;
        labels[visible].classList.add('is-hidden');
      }

      var hidden = labels.length - visible;
      more.textContent = '+' + hidden;
      more.setAttribute('aria-label', hidden + (hidden === 1 ? ' more colour' : ' more colours'));
    }

    _expandRail() {
      this._expanded = true;
      if (this._labels) {
        for (var i = 0; i < this._labels.length; i++) this._labels[i].classList.remove('is-hidden');
      }
      if (this._moreBtn) this._moreBtn.hidden = true;
    }

    _previewName(i) {
      if (!this.colorname) return;
      var c = this.colors[i];
      if (c) this.colorname.textContent = c.n;
    }

    _restoreName() {
      if (!this.colorname) return;
      var c = this.colors[this.colorIndex];
      if (c) this.colorname.textContent = c.n;
    }

    /* ---- spotlight scroll-scrub ---- */

    _armScrub() {
      var self = this;
      this._inView = false;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { self._inView = en.isIntersecting; });
      }, { threshold: 0 });
      this.io.observe(this.frame);

      var raf = false;
      this._onScroll = function () {
        if (raf) return;
        raf = true;
        requestAnimationFrame(function () { raf = false; self._scrub(); });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._scrub();
    }

    _scrub() {
      if (this._took || !this._inView) return;
      var media = (this.colors[this.colorIndex] || {}).m || [];
      if (media.length <= 1) return;

      var rect = this.frame.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var total = vh + rect.height;
      var passed = vh - rect.top;          // 0 as it enters bottom → grows travelling up
      var p = passed / total;
      p = (p - 0.18) / 0.64;               // rest on frame 1 / last at the extremes
      p = clamp(p, 0, 1);

      var max = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (max <= 0) return;
      this.viewport.classList.add('is-scrubbing');
      this.viewport.scrollLeft = p * max;
    }

    _takeover() {
      if (this._took) return;
      this._took = true;
      if (this.viewport) this.viewport.classList.remove('is-scrubbing');
      if (this._onScroll) { window.removeEventListener('scroll', this._onScroll); this._onScroll = null; }
      if (this.io) { this.io.disconnect(); this.io = null; }
    }
  }

  if ('customElements' in window && !customElements.get('hp-product-card')) {
    customElements.define('hp-product-card', HpCardElement);
  }
})();
